import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { RichHeading } from '@/components/common/Heading';
import { Loader } from '@/components/common/Loader';
import { Paragraph } from '@/components/common/MarkUp';
import { FormError, HubspotForm } from '@/components/forms/HubspotForm';
import { useFormData } from '@/components/forms/useFormData';
import { Section } from '@/components/layout/Section';
import { colourTokens } from '@/styles/colours';

import type { EmailSignUpForm as SanityEmailSignUpForm } from '@/sanity/sanity.types'

export interface EmailSignUpFormProps extends SanityEmailSignUpForm { }

export const EmailSignUpForm = ({
  heading,
  hubspotFormId,
  additionalText,
  backgroundColour,
  blockSpacing,
  paddingAdjustment,
}: EmailSignUpFormProps) => {
  const response = useFormData(hubspotFormId);
  const { data, error, isValidating } = response || {};

  if (data?.status === 'error' || error) return <FormError />;
  if (isValidating) return <Loader />;

  return (
    <Section
      verticalPadding={paddingAdjustment ?? true}
      spacing={blockSpacing}
      backgroundColour={backgroundColour?.token}
      className="email-sign-up-form"
    >
      <BannerWrapper>
        <FormHeading>
          <RichHeading
            elementType="paragraph"
            size="heading-md"
            heading={heading}
          />
        </FormHeading>

        <FormContainer>
          {data ? (
            <HubspotForm
              formData={data}
              colourTheme="dark"
              formType="emailSignUp"
            />
          ) : null}
        </FormContainer>
        {additionalText && (
          <AdditionalText size="subheading-sm">{additionalText}</AdditionalText>
        )}
      </BannerWrapper>
    </Section>
  );
};

const BannerWrapper = styled.div`
  display: flex;
  gap: var(--spacing-small);
  flex-direction: column;
  align-self: stretch;
  align-items: center;
  padding: var(--spacing-medium);
  background-color: var(${colourTokens.surface.inversePrimary.token});
  border-radius: var(--radius-m);
  box-shadow: 0px 1px 32px 8px rgba(0, 0, 0, 0.02);
  margin: 0 auto;

  .thank-you-container {
    padding: var(--spacing-small) 0;

    a {
      margin-top: var(--spacing-small);
    }
    & + p {
      display: none;
    }
  }

  ${screen.sm} {
    padding: var(--spacing-300);
  }

  ${screen.md} {
    padding: var(--spacing-large) var(--spacing-x-large);
    display: grid;
    grid-template-columns: calc(30% - (var(--spacing-small) / 2)) calc(
        70% - (var(--spacing-small) / 2)
      );
    align-items: flex-start;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-flow: column;
  gap: var(--spacing-medium);
`;
const FormHeading = styled.div`
  width: 100%;
  p {
    color: var(--colour-inverse-base);
  }
`;

const AdditionalText = styled(Paragraph)`
  color: var(--colour-inverse-subtle-text);
  text-align: center;
  margin-top: var(--spacing-x-small);

  ${screen.md} {
    text-align: left;
  }
`;
